import React, { useState, useMemo } from 'react';
import './ShiftsTable.css';
import { HoursMode, NetVarianceRow, OverrideData, computeNetHours } from './types';
import OverrideModal from './OverrideModal';

type SortKey =
  | 'date' | 'employee_name' | 'clocked_in_role' | 'scheduled_role'
  | 'start' | 'end' | 'clock_in' | 'clock_out'
  | 'hours_scheduled' | 'hours_worked' | 'variance' | 'net_hours';

interface SortState { key: SortKey; dir: 'asc' | 'desc' }

interface ShiftsTableProps {
  rows: NetVarianceRow[];
  loading?: boolean;
  onSetOverride: (rowId: string, data: OverrideData) => void;
  onClearOverride: (rowId: string) => void;
  onSetMode: (rowId: string, mode: HoursMode) => void;
  onRefresh: () => void;
}

type ModalState =
  | { mode: 'set'; rowId: string }
  | { mode: 'cancel'; rowId: string }
  | null;

interface OverrideTooltip {
  x: number;
  y: number;
  reason: string;
  overriddenBy: string | null;
}

// ─── Formatting helpers ──────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtVariance(v: number): string {
  return v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2);
}

// ─── Cell flag helpers (hues from varianceReportGenerator.py) ────────────────

function clockInFlag(row: NetVarianceRow): 'red' | 'yellow' | null {
  if (row.start === null) return null;
  if (row.clock_in === null) return 'red';
  const diffMin = (new Date(row.clock_in).getTime() - new Date(row.start).getTime()) / 60000;
  if (diffMin < 0) return 'red';
  if (diffMin > 30) return 'yellow';
  return null;
}

function clockOutFlag(row: NetVarianceRow): 'red' | null {
  if (row.end === null) return null;
  if (row.clock_out === null) return 'red';
  const diffMin = (new Date(row.clock_out).getTime() - new Date(row.end).getTime()) / 60000;
  return diffMin > 0 ? 'red' : null;
}

// ─── Sort ────────────────────────────────────────────────────────────────────

function compareRows(a: NetVarianceRow, b: NetVarianceRow, key: SortKey): number {
  switch (key) {
    case 'date':             return a.date.localeCompare(b.date);
    case 'employee_name':    return a.employee_name.localeCompare(b.employee_name);
    case 'clocked_in_role':  return a.clocked_in_role.localeCompare(b.clocked_in_role);
    case 'scheduled_role':   return a.scheduled_role.localeCompare(b.scheduled_role);
    case 'start':            return (a.start ?? '').localeCompare(b.start ?? '');
    case 'end':              return (a.end ?? '').localeCompare(b.end ?? '');
    case 'clock_in':         return (a.clock_in ?? '').localeCompare(b.clock_in ?? '');
    case 'clock_out':        return (a.clock_out ?? '').localeCompare(b.clock_out ?? '');
    case 'hours_scheduled':  return a.hours_scheduled - b.hours_scheduled;
    case 'hours_worked':     return a.hours_worked - b.hours_worked;
    case 'variance':         return a.variance - b.variance;
    case 'net_hours':        return computeNetHours(a) - computeNetHours(b);
    default:                 return 0;
  }
}

function SortIndicator({ sortKey, current, dir }: { sortKey: SortKey; current: SortKey; dir: 'asc' | 'desc' }) {
  if (sortKey !== current) return <span className="tc-sort-idle">⇅</span>;
  return <span className="tc-sort-active">{dir === 'asc' ? '▴' : '▾'}</span>;
}

function InfoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <rect x="7.1" y="7" width="1.8" height="5" rx="0.9" />
      <rect x="7.1" y="4" width="1.8" height="1.8" rx="0.9" />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ShiftsTable({ rows, loading = false, onSetOverride, onClearOverride, onSetMode, onRefresh }: ShiftsTableProps) {
  const [sort, setSort] = useState<SortState>({ key: 'date', dir: 'asc' });
  const [modal, setModal] = useState<ModalState>(null);
  const [overrideTooltip, setOverrideTooltip] = useState<OverrideTooltip | null>(null);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const secondary = a.date.localeCompare(b.date);
      const primary = compareRows(a, b, sort.key);
      if (sort.key === 'date') return sort.dir === 'asc' ? primary : -primary;
      if (primary !== 0) return sort.dir === 'asc' ? primary : -primary;
      return secondary; // secondary: always date asc
    });
  }, [rows, sort]);

  const handleSort = (key: SortKey) => {
    setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const th = (key: SortKey, label: React.ReactNode, extraClass = '') => (
    <th
      className={`tc-th tc-th--sortable${extraClass ? ` ${extraClass}` : ''}`}
      onClick={() => handleSort(key)}
    >
      <span className="tc-th-inner">
        {label}
        <SortIndicator sortKey={key} current={sort.key} dir={sort.dir} />
      </span>
    </th>
  );

  const activeRow = modal ? sortedRows.find(r => r.id === modal.rowId) : null;
  const activeOverride: OverrideData | undefined =
    activeRow && activeRow.hours_mode === HoursMode.OVERRIDE && activeRow.override !== null
      ? { hours: activeRow.override, reason: activeRow.reason ?? '' }
      : undefined;

  return (
    <>
      <div className="tc-table-container">
        {/* Table topbar with row count and Refresh */}
        <div className="tc-topbar">
          <span className="tc-topbar-count">
            {sortedRows.length} {sortedRows.length === 1 ? 'shift' : 'shifts'}
          </span>
          <button className="tc-refresh-btn" onClick={onRefresh} disabled={loading} title="Apply filters and reload">
            {loading ? '⟳ Loading…' : '↻ Refresh'}
          </button>
        </div>

        <div className="tc-table-scroll">
          <table className="tc-table">
            <thead>
              <tr className="tc-thead-row">
                {th('date',            'Date')}
                {th('employee_name',   'Employee')}
                {th('clocked_in_role', 'Shift Role')}
                {th('scheduled_role',  'Scheduled Role')}
                {th('start',           'Start')}
                {th('end',             'End')}
                {th('clock_in',        'Clock In')}
                {th('clock_out',       'Clock Out')}
                {th('hours_scheduled', 'Hrs Sched', 'tc-th--num')}
                {th('hours_worked',    'Hrs Worked', 'tc-th--num')}
                {th('variance',        'Variance',  'tc-th--num')}
                {th('net_hours',       'Net Hrs',   'tc-th--num tc-th--nethrs')}
                <th className="tc-th">Override</th>
                <th className="tc-th">Mode</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr><td colSpan={14} className="tc-empty-cell" /></tr>
              ) : (
                sortedRows.map(row => {
                  const isStatHoliday = row.is_stat_holiday;
                  const isOverride = row.hours_mode === HoursMode.OVERRIDE;
                  const netHrs = computeNetHours(row);
                  const ciFlag = clockInFlag(row);
                  const coFlag = clockOutFlag(row);
                  const varFlag = row.variance > 0 ? 'red' : null;

                  return (
                    <tr key={row.id} className="tc-row">
                      <td className={`tc-td${isStatHoliday ? ' tc-cell-stat' : ''}`}>{fmtDate(row.date)}</td>
                      <td className="tc-td">{row.employee_name}</td>
                      <td className={`tc-td${row.role_flag ? ' tc-cell-red' : ''}`}>{row.clocked_in_role}</td>
                      <td className={`tc-td${row.role_flag ? ' tc-cell-red' : ''}`}>{row.scheduled_role}</td>
                      <td className="tc-td tc-td--time">{fmtTime(row.start)}</td>
                      <td className="tc-td tc-td--time">{fmtTime(row.end)}</td>
                      <td className={`tc-td tc-td--time${ciFlag ? ` tc-cell-${ciFlag}` : ''}`}>{fmtTime(row.clock_in)}</td>
                      <td className={`tc-td tc-td--time${coFlag ? ` tc-cell-${coFlag}` : ''}`}>{fmtTime(row.clock_out)}</td>
                      <td className="tc-td tc-td--num">{row.hours_scheduled.toFixed(2)}</td>
                      <td className="tc-td tc-td--num">{row.hours_worked.toFixed(2)}</td>
                      <td className={`tc-td tc-td--num${varFlag ? ' tc-cell-red' : ''}`}>{fmtVariance(row.variance)}</td>
                      <td className="tc-td tc-td--num tc-td--nethrs">{netHrs.toFixed(2)}</td>
                      <td className="tc-td tc-td--override">
                        <div className="tc-override-cell">
                          {isOverride ? (
                            <button className="tc-btn-overridden" onClick={() => setModal({ mode: 'cancel', rowId: row.id })}>
                              Overridden ↩
                            </button>
                          ) : (
                            <button className="tc-btn-override" onClick={() => setModal({ mode: 'set', rowId: row.id })}>
                              Override
                            </button>
                          )}
                          {isOverride && (
                            <button
                              className="tc-info-icon-btn"
                              tabIndex={-1}
                              onMouseEnter={e => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setOverrideTooltip({
                                  x: rect.left + rect.width / 2,
                                  y: rect.top,
                                  reason: row.reason ?? '—',
                                  overriddenBy: row.overridden_by,
                                });
                              }}
                              onMouseLeave={() => setOverrideTooltip(null)}
                            >
                              <InfoIcon />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="tc-td tc-td--mode">
                        <button
                          className={`tc-mode-switch tc-mode-switch--${row.hours_mode === HoursMode.WORKED ? 'worked' : 'sched'}${isOverride ? ' tc-mode-switch--disabled' : ''}`}
                          disabled={isOverride}
                          onClick={() => !isOverride && onSetMode(
                            row.id,
                            row.hours_mode === HoursMode.WORKED ? HoursMode.SCHEDULED : HoursMode.WORKED,
                          )}
                        >
                          <span className="tc-mode-switch-knob" />
                          <span className="tc-mode-switch-text tc-mode-switch-text--worked">Worked</span>
                          <span className="tc-mode-switch-text tc-mode-switch-text--sched">Sched</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {overrideTooltip && (
        <div
          className="tc-info-tooltip"
          style={{ left: overrideTooltip.x, top: overrideTooltip.y - 8 }}
        >
          <p className="tc-info-tooltip-label">Reason</p>
          <p className="tc-info-tooltip-value">{overrideTooltip.reason}</p>
          {overrideTooltip.overriddenBy && (
            <>
              <p className="tc-info-tooltip-label">Overridden by</p>
              <p className="tc-info-tooltip-value">{overrideTooltip.overriddenBy}</p>
            </>
          )}
        </div>
      )}

      {modal?.mode === 'set' && (
        <OverrideModal
          mode="set"
          existing={activeOverride}
          onConfirm={data => { onSetOverride(modal.rowId, data); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.mode === 'cancel' && (
        <OverrideModal
          mode="cancel"
          onConfirm={() => { onClearOverride(modal.rowId); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}
